// @ts-ignore
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import styled, { keyframes } from 'styled-components';
import { api } from '../../../utils/apiUtils'; // Assuming this path is correct
import { EmailVerificationFormData } from '../../../types';
import { getLogoUrlSync, getLogoUrl } from '../../../utils/logoUtils';
import ChatWidget from '../../common/ChatWidget';
import SocialShareButtons from '../../common/SocialShareButtons';
import SocialProofToast from '../../common/SocialProofToast';
import PuneMapModal from '../../common/PuneMapModal';

// API URL configuration
const API_URL_FROM_ENV = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL_FROM_ENV.startsWith('http') ? API_URL_FROM_ENV : '';

// Generate or retrieve visitor ID for anonymous tracking
const getVisitorId = (): string => {
  let visitorId = sessionStorage.getItem('visitorId');
  if (!visitorId) {
    visitorId = `visitor_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    sessionStorage.setItem('visitorId', visitorId);
  }
  return visitorId;
};

// Capture and persist UTM parameters from URL
const getUtmParams = () => {
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(key => {
    const val = params.get(key);
    if (val) {
      utm[key] = val;
      sessionStorage.setItem(key, val); // persist across navigations
    }
  });
  // Fallback: read from sessionStorage if not in current URL
  if (!utm.utm_source) {
    const ss = sessionStorage.getItem('utm_source');
    if (ss) {
      utm.utm_source = ss;
      utm.utm_medium = sessionStorage.getItem('utm_medium') || '';
      utm.utm_campaign = sessionStorage.getItem('utm_campaign') || '';
      utm.utm_content = sessionStorage.getItem('utm_content') || '';
      utm.utm_term = sessionStorage.getItem('utm_term') || '';
    }
  }
  return Object.keys(utm).length > 0 ? utm : null;
};

// Track visitor activity (pre-login)
const trackVisitor = async (page: string, activity: string, email?: string) => {
  try {
    const visitorId = getVisitorId();
    const utmData = getUtmParams();
    await fetch(`${API_BASE}/api/visitor/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        visitorId, page, activity, email, referrer: document.referrer || null,
        ...(utmData || {}),
      })
    });
  } catch (error) {
    console.debug('Visitor tracking failed:', error);
  }
};

// Styled components for a more modern UI
const Container = styled.div`
  display: flex;
  flex-direction: row;
  height: 100vh;
  min-height: 100vh;
  min-height: -webkit-fill-available; /* iOS Safari fix */
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e7eb 100%);
  overflow: hidden;
  
  @media (max-width: 992px) {
    flex-direction: column;
    height: auto;
    min-height: 100vh;
  }
  
  @media (max-width: 480px) {
    min-height: 100dvh; /* Dynamic viewport height for mobile */
  }
`;

const LeftSection = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  align-items: stretch;
  color: white;
  padding: 0;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 992px) {
    flex: 0 0 auto;
  }
`;

const AdContainer = styled.div`
  flex-grow: 1; /* Takes up all available vertical space */
  width: 100%;
  display: flex;
  align-items: stretch; /* Makes child (the link) fill the height */
  justify-content: center;
`;

const AdImage = styled.img`
  width: 100%; /* Full width of the LeftSection */
  height: auto; /* Maintain aspect ratio */
  object-fit: contain; /* Show full image, scaled to fit width */
  /* No margin-bottom, border, padding, or background for seamless top fit */
`;

// Carousel animation keyframes
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const slideOut = keyframes`
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(-100%);
  }
`;

// Carousel Styled Components
const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  overflow: hidden;
  cursor: pointer;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #4a6cf7 100%);
  padding: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 992px) {
    padding: 12px;
  }
  
  @media (max-width: 480px) {
    padding: 8px;
  }
  
  &:hover .carousel-arrow {
    opacity: 1;
  }
`;

const CarouselTrack = styled.div<{ $currentIndex: number }>`
  display: flex;
  transition: transform 1.2s cubic-bezier(0.25, 0.1, 0.25, 1);
  transform: translateX(${props => -props.$currentIndex * 100}%);
  width: 100%;
`;

const CarouselSlide = styled.div`
  min-width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px;
`;

const CarouselImage = styled.img`
  max-width: 98%;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3),
    0 10px 20px rgba(0, 0, 0, 0.2),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  background: white;
`;

const CarouselArrow = styled.button<{ $direction: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.$direction === 'left' ? 'left: 10px;' : 'right: 10px;'}
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: all 0.3s ease;
  z-index: 10;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background: #ffffff;
    transform: translateY(-50%) scale(1.1);
  }
  
  svg {
    width: 20px;
    height: 20px;
    color: #4a6cf7;
  }
`;

const CarouselDots = styled.div`
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 10;
`;

const CarouselDot = styled.button<{ $active: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: 2px solid white;
  background: ${props => props.$active ? 'white' : 'transparent'};
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
  
  &:hover {
    background: rgba(255, 255, 255, 0.7);
  }
`;

// Image Zoom Modal Components
const ImageModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  cursor: zoom-out;
`;

const ImageModalContent = styled(motion.div)`
  position: relative;
  max-width: 95vw;
  max-height: 95vh;
  overflow: hidden;
  cursor: grab;
  
  &:active {
    cursor: grabbing;
  }
`;

const ZoomableImage = styled.img<{ $scale: number; $translateX: number; $translateY: number }>`
  max-width: 95vw;
  max-height: 95vh;
  object-fit: contain;
  transform: scale(${props => props.$scale}) translate(${props => props.$translateX}px, ${props => props.$translateY}px);
  transition: transform 0.1s ease-out;
  user-select: none;
  -webkit-user-drag: none;
`;

const VerticalZoomContainer = styled.div`
  position: fixed;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  z-index: 10001;
  background: rgba(255, 255, 255, 0.95);
  padding: 12px 10px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const ZoomSliderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const ZoomSlider = styled.input.attrs({ type: 'range' })`
  writing-mode: vertical-lr;
  direction: rtl;
  width: 8px;
  height: 150px;
  appearance: none;
  background: linear-gradient(to top, #e0e0e0 0%, #3b82f6 100%);
  border-radius: 4px;
  cursor: pointer;
  
  &::-webkit-slider-thumb {
    appearance: none;
    width: 20px;
    height: 20px;
    background: #3b82f6;
    border-radius: 50%;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(59, 130, 246, 0.5);
    transition: all 0.2s ease;
  }
  
  &::-webkit-slider-thumb:hover {
    transform: scale(1.2);
    background: #2563eb;
  }
  
  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    background: #3b82f6;
    border-radius: 50%;
    cursor: pointer;
    border: none;
  }
`;

const ZoomLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #374151;
`;

const ZoomPercentage = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #3b82f6;
  min-width: 40px;
  text-align: center;
`;

const ZoomResetBtn = styled.button`
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #374151;
  
  &:hover {
    background: #e5e7eb;
  }
`;

const ImageModalClose = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10001;
  transition: all 0.2s ease;
  
  &:hover {
    background: white;
    transform: scale(1.1);
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const BrochureLink = styled.a`
  flex-grow: 1; /* Fill the AdContainer */
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background: transparent;
  color: #ffffff;
  border: none;
  border-radius: 0; /* No rounded corners */
  font-size: 18px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.3s ease;
  box-shadow: none; /* No shadow */

  &:hover {
    background: rgba(255, 255, 255, 0.1); /* Subtle hover effect */
    color: #ffffff;
    transform: none;
    box-shadow: none;
  }

  &:active {
    transform: none;
    box-shadow: none;
    background: rgba(0, 0, 0, 0.1); /* Subtle active effect */
  }
  
  svg {
    margin-right: 10px;
    vertical-align: middle;
  }
`;

const RightSection = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background-color: #ffffff;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 70% 30%, rgba(74, 108, 247, 0.03), transparent 70%);
    z-index: 0;
  }
  
  @media (max-width: 992px) {
    flex-grow: 1;
    overflow-y: auto;
    padding: 15px;
    -webkit-overflow-scrolling: touch;
  }
  
  @media (max-width: 480px) {
    padding: 12px;
    align-items: flex-start;
    padding-top: 20px;
  }
  
  @media (max-width: 375px) {
    padding: 10px;
  }
`;

const AnimatedCircle = styled(motion.div)`
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
`;

const Card = styled(motion.div)`
  background-color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-radius: 18px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.12), 0 5px 15px rgba(0, 0, 0, 0.06);
  padding: 18px 20px;
  width: 100%;
  max-width: 400px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle at top left, rgba(74, 108, 247, 0.05), transparent 70%);
    z-index: -1;
  }
  
  @media (max-width: 992px) {
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    padding: 20px;
    margin: auto; /* Center card if space, or allow it to take needed height */
    max-height: none; /* Allow card to be its full height, RightSection handles scroll */
  }
`;

const AnimatedHeading = styled(motion.h1)`
  font-size: 36px; /* Reduced font size */
  color: #fff; /* Assuming white text on a dark/animated background */
  margin-bottom: 12px;
  text-align: center;
  position: relative;
  z-index: 10;
  text-shadow: 0px 2px 8px rgba(0, 0, 0, 0.6);
  
  @media (max-width: 992px) {
    font-size: 24px;
  }
  @media (max-width: 480px) {
    font-size: 20px;
  }
`;

const AnimatedText = styled(motion.p)`
  font-size: 16px;
  line-height: 1.5;
  text-align: center;
  margin-bottom: 20px;
  max-width: 600px;
  position: relative;
  z-index: 10;
  text-shadow: 0px 2px 6px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 992px) {
    font-size: 13px;
    margin-bottom: 10px;
  }
  @media (max-width: 480px) {
    font-size: 12px;
    margin-bottom: 8px;
  }
`;

const AnimatedFeature = styled(motion.div)`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  background: rgba(255, 255, 255, 0.1);
  padding: 10px 16px;
  border-radius: 8px;
  width: 100%;
  max-width: 500px;
  position: relative;
  z-index: 10;
  
  @media (max-width: 992px) {
    padding: 8px 10px;
    margin-bottom: 6px;
  }
  @media (max-width: 480px) {
    padding: 6px 8px;
    margin-bottom: 4px;
  }
`;

const FeatureIcon = styled.div`
  margin-right: 12px;
  font-size: 20px;
`;

const FeatureText = styled.p`
  font-size: 14px;
  margin: 0;
  @media (max-width: 992px) {
    font-size: 13px;
  }
  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const LogoContainer = styled.div`
  text-align: center;
  margin-bottom: 20px;
`;

const Logo = styled.img`
  max-width: 420px;
  max-height: 90px;
  height: auto;
  object-fit: contain;
  
  @media (max-width: 480px) {
    max-width: 280px;
    max-height: 60px;
  }
  
  @media (max-width: 375px) {
    max-width: 240px;
    max-height: 50px;
  }
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: #2d3748;
  text-align: center;
  margin-bottom: 6px;
  padding-top: 6px;
  background: linear-gradient(135deg, #4a6cf7 0%, #2b3bdf 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.5px;
  
  @media (max-width: 480px) {
    font-size: 22px;
    margin-bottom: 6px;
  }
  
  @media (max-width: 375px) {
    font-size: 20px;
  }
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #718096;
  text-align: center;
  margin-bottom: 20px;
  line-height: 1.5;
  max-width: 90%;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 480px) {
    font-size: 13px;
    margin-bottom: 16px;
  }
  
  @media (max-width: 375px) {
    font-size: 12px;
    margin-bottom: 12px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
  position: relative;
  transition: all 0.3s ease;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #4a5568;
  transition: all 0.2s ease;
  
  ${FormGroup}:focus-within & {
    color: #4a6cf7;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  font-size: 16px; /* 16px prevents iOS zoom on focus */
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.3s ease;
  background-color: rgba(247, 250, 252, 0.8);
  -webkit-appearance: none; /* Remove iOS default styling */
  
  @media (max-width: 480px) {
    padding: 14px 12px;
    border-radius: 10px;
  }
  
  &::placeholder {
    color: #a0aec0;
    opacity: 0.8;
  }
  
  &:hover {
    border-color: #cbd5e0;
    background-color: rgba(247, 250, 252, 1);
  }
  
  &:focus {
    border-color: #4a6cf7;
    box-shadow: 0 0 0 3px rgba(74, 108, 247, 0.15);
    outline: none;
    background-color: #ffffff;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 14px;
  background: linear-gradient(135deg, #4a6cf7 0%, #2b3bdf 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 4px 10px rgba(74, 108, 247, 0.2);
  position: relative;
  overflow: hidden;
  letter-spacing: 0.5px;
  min-height: 48px; /* Touch-friendly height */
  -webkit-tap-highlight-color: transparent;
  
  @media (max-width: 480px) {
    padding: 16px;
    font-size: 15px;
    border-radius: 10px;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(74, 108, 247, 0.3);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 5px rgba(74, 108, 247, 0.2);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.2), transparent);
    transform: translateX(-100%);
    transition: transform 0.6s;
  }
  
  &:hover::before {
    transform: translateX(100%);
  }
  
  &:disabled {
    background-color: #a0aec0;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 6px;
  background-color: #e2e8f0;
  border-radius: 3px;
  margin-bottom: 16px;
  overflow: hidden;
`;

const ProgressBarFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, #4a6cf7, #3a56d4);
  border-radius: 3px;
  box-shadow: 0 0 10px rgba(74, 108, 247, 0.5);
`;

const ErrorMessage = styled.div`
  color: #e63946;
  font-size: 14px;
  margin-top: 5px;
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 20px;
  font-size: 12px;
  color: #718096;
`;

const AdminLink = styled.a`
  font-size: 12px;
  color: #718096;
  text-decoration: none;
  margin-top: 16px;
  display: inline-block;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    color: #4a6cf7;
  }
  
  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 1px;
    bottom: -2px;
    left: 0;
    background-color: #4a6cf7;
    transition: width 0.3s ease;
  }
  
  &:hover::after {
    width: 100%;
  }
  display: block;
  text-align: center;
  
  &:hover {
    text-decoration: underline;
    color: #4c6ef5;
  }
`;

const DownloadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="7 10 12 15 17 10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
);

// Golden border flair animation keyframes
const goldenFlairAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

// Terms Agreement Styled Components
const AgreementContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 10px;
  margin-left: 1.4px;
  margin-right: 1.4px;
  padding: 10px 10px 10px 12px;
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  
  /* Golden flair border effect */
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: conic-gradient(
      from 0deg,
      transparent 0deg 60deg,
      #fbbf24 80deg,
      #f59e0b 90deg,
      #d97706 100deg,
      #fbbf24 110deg,
      #fef3c7 120deg,
      transparent 140deg 360deg
    );
    animation: ${goldenFlairAnimation} 3s linear infinite;
    z-index: 0;
  }
  
  /* Inner container to cover the spinning gradient */
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    right: 2px;
    bottom: 2px;
    background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
    border-radius: 8px;
    z-index: 1;
  }
  
  /* Ensure content is above the pseudo-elements */
  & > * {
    position: relative;
    z-index: 2;
  }
`;

const AgreementCheckbox = styled.input`
  width: 18px;
  height: 18px;
  margin-top: 2px;
  cursor: pointer;
  accent-color: #4a6cf7;
  flex-shrink: 0;
`;

const AgreementText = styled.label`
  font-size: 12px;
  color: #92400e;
  line-height: 1.5;
  cursor: pointer;
  
  a {
    color: #4a6cf7;
    text-decoration: underline;
    font-weight: 600;
    cursor: pointer;
    
    &:hover {
      color: #2b3bdf;
    }
  }
`;

// Brochure Modal Styled Components
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContainer = styled(motion.div)<{ $size: string }>`
  background: white;
  border-radius: 16px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  
  ${props => {
    switch (props.$size) {
      case 'compact':
        return `
          width: 500px;
          height: 400px;
        `;
      case 'medium':
        return `
          width: 700px;
          height: 550px;
        `;
      case 'large':
        return `
          width: 900px;
          height: 700px;
        `;
      case 'fullscreen':
        return `
          width: 95vw;
          height: 90vh;
        `;
      default:
        return `
          width: 700px;
          height: 550px;
        `;
    }
  }}
  
  @media (max-width: 768px) {
    width: 95vw !important;
    height: 85vh !important;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(135deg, #4a6cf7 0%, #2b3bdf 100%);
  color: white;
  gap: 12px;
  flex-wrap: wrap;
  
  @media (max-width: 600px) {
    padding: 10px 12px;
  }
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ModalCloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow: hidden;
  background: #f1f5f9;
`;

const PDFViewer = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
`;

const ModalControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DisabledInputOverlay = styled.div`
  position: relative;
  
  &::after {
    content: 'Please agree to Terms & Conditions first';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 11px;
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
    z-index: 10;
  }
  
  &:hover::after {
    opacity: 1;
  }
`;

const DisabledInput = styled(Input)`
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #f1f5f9;
  
  &:hover, &:focus {
    border-color: #e2e8f0;
    box-shadow: none;
  }
`;

// --- Fuzzy email domain matching (Mailcheck-style) ---
const KNOWN_DOMAINS = [
  'gmail.com', 'yahoo.com', 'yahoo.co.in', 'yahoo.in',
  'hotmail.com', 'outlook.com', 'live.com', 'msn.com',
  'rediffmail.com', 'rediff.com',
  'aol.com', 'icloud.com', 'me.com', 'mac.com',
  'protonmail.com', 'proton.me',
  'zoho.com', 'yandex.com', 'mail.com',
  'gmx.com', 'gmx.net',
];

const levenshtein = (a: string, b: string): number => {
  const m = a.length, n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
  return dp[m][n];
};

const suggestDomain = (email: string): string | null => {
  const parts = email.split('@');
  if (parts.length !== 2 || !parts[1]) return null;
  const domain = parts[1].toLowerCase();
  if (KNOWN_DOMAINS.includes(domain)) return null; // exact match, no suggestion
  let best: string | null = null;
  let bestDist = Infinity;
  for (const known of KNOWN_DOMAINS) {
    const dist = levenshtein(domain, known);
    if (dist > 0 && dist <= 2 && dist < bestDist) {
      bestDist = dist;
      best = known;
    }
  }
  return best ? `${parts[0]}@${best}` : null;
};

const EmailVerification: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<EmailVerificationFormData>({
    email: '',
    phoneNumber: ''
  });
  const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);
  const [errors, setErrors] = useState<{
    email?: string;
    phoneNumber?: string;
    general?: string;
  }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [progress, setProgress] = useState(0);
  const [logoUrl, setLogoUrl] = useState<string>(getLogoUrlSync());
  
  // Terms agreement state
  const [hasAgreed, setHasAgreed] = useState(false);
  const [showBrochureModal, setShowBrochureModal] = useState(false);
  const [showPuneMapModal, setShowPuneMapModal] = useState(false);
  const [showEligibilityModal, setShowEligibilityModal] = useState(false);
  const [modalSize] = useState<'fullscreen'>('fullscreen');
  
  // Carousel state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);
  const carouselImages = ['/brochures/1.png', '/brochures/2.png'];
  
  // Image zoom modal state
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [imageZoom, setImageZoom] = useState(1);
  const [imagePan, setImagePan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Application deadline countdown
  const [applicationDeadline, setApplicationDeadline] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<{ days: number; hours: number; minutes: number; seconds: number } | null>(null);
  const [deadlinePassed, setDeadlinePassed] = useState(false);

  // Fetch deadline from public settings
  useEffect(() => {
    const fetchDeadline = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/maintenance-status`);
        const data = await res.json();
        if (data.applicationDeadline && !data.deadlinePassed) {
          setApplicationDeadline(data.applicationDeadline);
        } else if (data.deadlinePassed) {
          setDeadlinePassed(true);
        }
      } catch {}
    };
    fetchDeadline();
  }, []);

  // Countdown timer tick
  useEffect(() => {
    if (!applicationDeadline) return;
    const tick = () => {
      const now = new Date().getTime();
      const target = new Date(applicationDeadline).getTime();
      const diff = target - now;
      if (diff <= 0) {
        setDeadlinePassed(true);
        setCountdown(null);
        return;
      }
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [applicationDeadline]);

  // Track when visitor first types email
  const hasTrackedEmailInput = useRef(false);
  
  // Track visitor landing on login page
  useEffect(() => {
    trackVisitor('Login Page', 'Landed on login page');
    
    // Set up heartbeat to keep visitor active
    const heartbeatInterval = setInterval(() => {
      const currentEmail = sessionStorage.getItem('trackingEmail') || undefined;
      trackVisitor('Login Page', 'Viewing login page', currentEmail);
    }, 30000); // Every 30 seconds
    
    // Handle tab close/browser close - remove visitor immediately
    const handleBeforeUnload = () => {
      const visitorId = sessionStorage.getItem('visitorId');
      if (visitorId) {
        // Use sendBeacon for reliable delivery on page unload
        const data = JSON.stringify({ visitorId });
        navigator.sendBeacon(`${API_BASE}/api/visitor/leave`, data);
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      clearInterval(heartbeatInterval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);
  
  // Handle View Brochure button click when T&C not agreed
  const handleViewBrochureClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowBrochureModal(true);
  };
  
  // Handle ESC key to close modals
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showImageModal) {
          setShowImageModal(false);
          setImageZoom(1);
          setImagePan({ x: 0, y: 0 });
        } else if (showBrochureModal) {
          setShowBrochureModal(false);
        }
      }
      // Arrow keys for carousel navigation
      if (e.key === 'ArrowLeft') {
        setCurrentSlide(prev => (prev - 1 + carouselImages.length) % carouselImages.length);
      } else if (e.key === 'ArrowRight') {
        setCurrentSlide(prev => (prev + 1) % carouselImages.length);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showBrochureModal, showImageModal, carouselImages.length]);
  
  // Carousel auto-play with 5 second interval
  useEffect(() => {
    if (isCarouselPaused || showImageModal) return;
    
    const intervalId = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % carouselImages.length);
    }, 5000); // 5 seconds - enough time to view each slide
    
    return () => clearInterval(intervalId);
  }, [isCarouselPaused, showImageModal, carouselImages.length]);
  
  // Carousel navigation handlers
  const handlePrevSlide = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentSlide(prev => (prev - 1 + carouselImages.length) % carouselImages.length);
  };
  
  const handleNextSlide = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentSlide(prev => (prev + 1) % carouselImages.length);
  };
  
  // Image modal handlers
  const handleImageClick = () => {
    setSelectedImageIndex(currentSlide);
    setShowImageModal(true);
    setImageZoom(1);
    setImagePan({ x: 0, y: 0 });
  };
  
  const handleZoomIn = () => {
    setImageZoom(prev => Math.min(prev + 0.5, 4));
  };
  
  const handleZoomOut = () => {
    setImageZoom(prev => {
      const newZoom = Math.max(prev - 0.5, 1);
      if (newZoom === 1) setImagePan({ x: 0, y: 0 });
      return newZoom;
    });
  };
  
  const handleResetZoom = () => {
    setImageZoom(1);
    setImagePan({ x: 0, y: 0 });
  };
  
  const handleImageMouseDown = (e: React.MouseEvent) => {
    if (imageZoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - imagePan.x, y: e.clientY - imagePan.y });
    }
  };
  
  const handleImageMouseMove = (e: React.MouseEvent) => {
    if (isDragging && imageZoom > 1) {
      setImagePan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };
  
  const handleImageMouseUp = () => {
    setIsDragging(false);
  };
  
  const handleCloseImageModal = () => {
    setShowImageModal(false);
    setImageZoom(1);
    setImagePan({ x: 0, y: 0 });
  };

  // SMTP settings are loaded server-side from the database by the /api/send-otp endpoint.
  // No need to fetch them on the frontend (they contain sensitive credentials).

  useEffect(() => {
    // Fetch logo from database
    const fetchLogo = async () => {
      try {
        const logo = await getLogoUrl();
        setLogoUrl(logo);
      } catch (error) {
        console.error('Error fetching logo:', error);
      }
    };
    
    fetchLogo();
  }, []);

  const validateForm = (): boolean => {
    const newErrors: {
      email?: string;
      phoneNumber?: string;
      general?: string;
    } = {};
    
    // Phone number validation (10-digit Indian mobile)
    if (!formData.phoneNumber) {
      newErrors.phoneNumber = 'Mobile number is required';
    } else if (!/^[6-9]\d{9}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Enter a valid 10-digit Indian mobile number';
    }
    
    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // For phone number, allow only digits and max 10
    if (name === 'phoneNumber') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData((prev: EmailVerificationFormData) => ({
        ...prev,
        phoneNumber: digitsOnly
      }));
      return;
    }
    setFormData((prev: EmailVerificationFormData) => ({
      ...prev,
      [name]: value
    }));
    
    // Track first email input
    if (name === 'email' && value.length > 0 && !hasTrackedEmailInput.current) {
      hasTrackedEmailInput.current = true;
      trackVisitor('Login Page', 'Started entering email');
    }
    
    // Store email for tracking purposes + fuzzy domain check
    if (name === 'email' && value.includes('@')) {
      sessionStorage.setItem('trackingEmail', value);
      trackVisitor('Login Page', 'Entering email address', value);
      setEmailSuggestion(suggestDomain(value));
    } else if (name === 'email') {
      setEmailSuggestion(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Track OTP request
    trackVisitor('Login Page', 'Requesting OTP', formData.email);
    
    setIsLoading(true);
    setErrors({});
    setShowProgress(true);
    setProgress(0);
    
    // Start progress animation
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        // Slow down progress as it approaches 90%
        if (prev < 30) return prev + 5;
        if (prev < 60) return prev + 3;
        if (prev < 90) return prev + 1;
        return prev;
      });
    }, 50);
    
    try {
      console.log('Sending OTP to:', formData.email);

      // Call the backend API to send OTP (backend loads SMTP settings from DB)
      const response = await fetch(`${API_BASE}/api/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Complete progress bar to 100%
        setProgress(100);
        
        // Store email and phone in sessionStorage for the OTP verification page
        sessionStorage.setItem('userEmail', formData.email);
        sessionStorage.setItem('userPhoneNumber', formData.phoneNumber);
        
        // Wait for progress animation to complete before navigating
        setTimeout(() => {
          // Navigate to OTP verification page
          navigate('/verify-otp');
        }, 500);
      } else {
        clearInterval(progressInterval);
        setShowProgress(false);
        setErrors({
          general: data.message || 'Failed to send OTP. Please try again.'
        });
      }
    } catch (error) {
      clearInterval(progressInterval);
      setShowProgress(false);
      console.error('Error sending OTP:', error);
      setErrors({
        general: error instanceof Error ? error.message : 'Failed to send OTP. Please try again.'
      });
    } finally {
      if (!showProgress) {
        setIsLoading(false);
      }
    }
  };

  // Animation variants for staggered animations
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };
  
  const circleVariants = {
    animate: {
      scale: [1, 1.2, 1],
      opacity: [0.7, 0.5, 0.7],
      transition: { duration: 8, repeat: Infinity, easings: ['easeInOut'] }
    }
  };
  
  const leftSectionVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: { 
      opacity: 1, 
      x: 0, 
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const adContainerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.5, delay: 0.4, ease: "easeOut" }
    }
  };

  return (
    <>
    <Container>
      <LeftSection>
        {/* Image Carousel */}
        <CarouselContainer
          onMouseEnter={() => setIsCarouselPaused(true)}
          onMouseLeave={() => setIsCarouselPaused(false)}
          onClick={handleImageClick}
        >
          <CarouselTrack $currentIndex={currentSlide}>
            {carouselImages.map((img, index) => (
              <CarouselSlide key={index}>
                <CarouselImage src={img} alt={`Brochure ${index + 1}`} />
              </CarouselSlide>
            ))}
          </CarouselTrack>
          
          {/* Navigation Arrows */}
          <CarouselArrow 
            className="carousel-arrow" 
            $direction="left" 
            onClick={handlePrevSlide}
            title="Previous (Left Arrow)"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </CarouselArrow>
          <CarouselArrow 
            className="carousel-arrow" 
            $direction="right" 
            onClick={handleNextSlide}
            title="Next (Right Arrow)"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </CarouselArrow>
          
          {/* Dot Indicators */}
          <CarouselDots>
            {carouselImages.map((_, index) => (
              <CarouselDot 
                key={index} 
                $active={currentSlide === index}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentSlide(index);
                }}
                title={`Slide ${index + 1}`}
              />
            ))}
          </CarouselDots>
        </CarouselContainer>
      </LeftSection>
      
      {/* Image Zoom Modal */}
      {showImageModal && (
        <ImageModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleCloseImageModal}
        >
          <ImageModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={handleImageMouseDown}
            onMouseMove={handleImageMouseMove}
            onMouseUp={handleImageMouseUp}
            onMouseLeave={handleImageMouseUp}
          >
            <ZoomableImage
              src={carouselImages[selectedImageIndex]}
              alt={`Brochure ${selectedImageIndex + 1}`}
              $scale={imageZoom}
              $translateX={imagePan.x / imageZoom}
              $translateY={imagePan.y / imageZoom}
              draggable={false}
            />
          </ImageModalContent>
          
          <VerticalZoomContainer onClick={(e) => e.stopPropagation()}>
            <ZoomLabel>➕</ZoomLabel>
            <ZoomSliderWrapper>
              <ZoomSlider
                min="100"
                max="400"
                step="10"
                value={imageZoom * 100}
                onChange={(e) => setImageZoom(Number(e.target.value) / 100)}
              />
            </ZoomSliderWrapper>
            <ZoomLabel>➖</ZoomLabel>
            <ZoomPercentage>{Math.round(imageZoom * 100)}%</ZoomPercentage>
            <ZoomResetBtn onClick={handleResetZoom}>Reset</ZoomResetBtn>
          </VerticalZoomContainer>
          
          <ImageModalClose onClick={handleCloseImageModal} title="Close (ESC)">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </ImageModalClose>
        </ImageModalOverlay>
      )}
      
      <RightSection>
        <Card
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
                    
          {/* Diagonal "FOR PUNE ONLY" Ribbon */}
          <div
            onClick={() => setShowPuneMapModal(true)}
            style={{
              position: 'absolute', top: 28, left: -42,
              width: 190, textAlign: 'center',
              transform: 'rotate(-45deg)',
              background: 'linear-gradient(135deg, #ef4444, #dc2626)',
              color: '#fff', fontSize: '10.5px', fontWeight: 800,
              letterSpacing: '1.2px', textTransform: 'uppercase',
              padding: '6px 0', cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(220,38,38,0.4)',
              zIndex: 10, userSelect: 'none',
              transition: 'filter 0.2s',
              overflow: 'visible',
            }}
            onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; }}
            title="Click to view Pune GA Map"
          >
            For Pune Only
          </div>

          {/* Application Deadline Countdown Timer */}
          {countdown && !deadlinePassed && (
            <div style={{
              margin: '-18px -20px 14px',
              padding: '14px 16px 10px',
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              borderRadius: '18px 18px 12px 12px',
              borderBottom: '1px solid #f59e0b',
              position: 'relative',
              overflow: 'hidden',
            }}>
              <div style={{
                position: 'absolute', inset: 0, opacity: 0.04,
                background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, #000 10px, #000 11px)',
              }} />
              <div style={{
                fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
                letterSpacing: '1.5px', color: '#92400e', marginBottom: '8px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#92400e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                Application Deadline
              </div>
              <div style={{
                display: 'flex', justifyContent: 'center', gap: '6px',
              }}>
                {[
                  { value: countdown.days, label: 'Days' },
                  { value: countdown.hours, label: 'Hrs' },
                  { value: countdown.minutes, label: 'Min' },
                  { value: countdown.seconds, label: 'Sec' },
                ].map((item, i) => (
                  <div key={i} style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    minWidth: '52px',
                  }}>
                    <div style={{
                      background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
                      color: '#fbbf24',
                      fontSize: '1.35rem', fontWeight: 800,
                      fontVariantNumeric: 'tabular-nums',
                      width: '52px', height: '48px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      borderRadius: '10px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)',
                      position: 'relative',
                      overflow: 'hidden',
                    }}>
                      <div style={{
                        position: 'absolute', top: '50%', left: 0, right: 0, height: '1px',
                        background: 'rgba(255,255,255,0.07)',
                      }} />
                      {String(item.value).padStart(2, '0')}
                    </div>
                    <span style={{
                      fontSize: '0.6rem', fontWeight: 700, color: '#78350f',
                      textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '4px',
                    }}>
                      {item.label}
                    </span>
                  </div>
                ))}
              </div>
              <div style={{
                fontSize: '0.68rem', color: '#92400e', textAlign: 'center',
                marginTop: '8px', fontWeight: 500,
              }}>
                {new Date(applicationDeadline!).toLocaleString('en-IN', {
                  weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
                  hour: '2-digit', minute: '2-digit', hour12: true
                })}
              </div>
            </div>
          )}

          {deadlinePassed && (
            <div style={{
              margin: '-18px -20px 14px',
              padding: '14px 16px',
              background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
              borderRadius: '18px 18px 12px 12px',
              borderBottom: '1px solid #fca5a5',
              textAlign: 'center',
            }}>
              <div style={{
                fontSize: '0.8rem', fontWeight: 700, color: '#991b1b',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="15" y1="9" x2="9" y2="15" />
                  <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
                Application Deadline Has Passed
              </div>
              <div style={{ fontSize: '0.75rem', color: '#7f1d1d', marginTop: '4px' }}>
                MNGL is no longer accepting new applications for CNG Dealership.
              </div>
            </div>
          )}

          <Title>Apply for CNG Dealership</Title>
          
          {errors.general && (
            <ErrorMessage style={{ textAlign: 'center', marginBottom: '15px' }}>{errors.general}</ErrorMessage>
          )}
          
          {/* Terms & Conditions Agreement */}
          <AgreementContainer>
            <AgreementCheckbox
              type="checkbox"
              id="termsAgreement"
              checked={hasAgreed}
              onChange={(e) => {
                const checked = e.target.checked;
                setHasAgreed(checked);
                if (checked) setShowEligibilityModal(true);
              }}
            />
            <AgreementText htmlFor="termsAgreement">
              I have read and agree to MNGL's{' '}
              <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer">
                Terms & Conditions
              </a>{' '}
              and the{' '}
              <a onClick={(e) => { e.preventDefault(); setShowBrochureModal(true); }}>
                MNGL DODO Application Brochure
              </a>
              . I understand and accept the guidelines for CNG dealership application.
            </AgreementText>
          </AgreementContainer>
          
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', gap: '10px' }}>
            <FormGroup style={{ flex: '0 0 34%', minWidth: 0 }}>
              <Label htmlFor="phoneNumber">Mobile Number</Label>
              {hasAgreed ? (
                <Input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="10-digit mobile"
                  disabled={isLoading}
                  required
                  maxLength={10}
                  style={{ letterSpacing: '1px' }}
                />
              ) : (
                <DisabledInputOverlay>
                  <DisabledInput
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value=""
                    placeholder="Agree to T&C first"
                    disabled={true}
                    readOnly
                  />
                </DisabledInputOverlay>
              )}
              {errors.phoneNumber && <ErrorMessage>{errors.phoneNumber}</ErrorMessage>}
            </FormGroup>

            <FormGroup style={{ flex: 1, minWidth: 0 }}>
              <Label htmlFor="email">Email Address</Label>
              {hasAgreed ? (
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  disabled={isLoading}
                  required
                />
              ) : (
                <DisabledInputOverlay>
                  <DisabledInput
                    type="email"
                    id="email"
                    name="email"
                    value=""
                    placeholder="Agree to T&C first"
                    disabled={true}
                    readOnly
                  />
                </DisabledInputOverlay>
              )}
              {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
              {emailSuggestion && !errors.email && (
                <div
                  style={{
                    marginTop: '6px', padding: '7px 12px', borderRadius: '8px',
                    background: '#fef3c7', border: '1px solid #fde68a',
                    fontSize: '13px', color: '#92400e', cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onClick={() => {
                    setFormData((prev: EmailVerificationFormData) => ({ ...prev, email: emailSuggestion }));
                    setEmailSuggestion(null);
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#fde68a'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#fef3c7'; }}
                >
                  Did you mean <strong style={{ color: '#1d4ed8' }}>{emailSuggestion}</strong>?
                </div>
              )}
            </FormGroup>
            </div>

            {showProgress && (
              <ProgressBarContainer>
                <ProgressBarFill 
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ ease: "easeOut" }}
                />
              </ProgressBarContainer>
            )}
            
            <FormGroup>
              <div style={{ position: 'relative' }}>
                {!hasAgreed ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button 
                      type="button"
                      onClick={handleViewBrochureClick}
                      style={{
                        flex: 1,
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        fontSize: '13px',
                        padding: '10px 12px',
                        margin: 0,
                        boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                      }}
                    >
                      📄 View Brochure
                    </Button>
                    <a 
                      href="/brochures/mngl-dodo-brochure.pdf" 
                      download="MNGL-DODO-Brochure.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        padding: '10px 12px',
                        background: 'linear-gradient(135deg, #4a6cf7 0%, #2b3bdf 100%)',
                        color: 'white',
                        borderRadius: '8px',
                        textDecoration: 'none',
                        fontSize: '13px',
                        fontWeight: 600,
                        boxShadow: '0 4px 12px rgba(74, 108, 247, 0.3)',
                        transition: 'all 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(74, 108, 247, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(74, 108, 247, 0.3)';
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="7 10 12 15 17 10"></polyline>
                        <line x1="12" y1="15" x2="12" y2="3"></line>
                      </svg>
                      Download (PDF)
                    </a>
                  </div>
                ) : (
                  <Button 
                    type="submit" 
                    disabled={isLoading}
                    style={{
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      transform: 'scale(1)',
                      boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
                    }}
                  >
                    {isLoading ? 'Sending OTP...' : '✉️ Send OTP'}
                  </Button>
                )}
              </div>
            </FormGroup>
          </form>
          
          <Footer style={{ marginTop: '16px' }}>
            <a 
              href="/area-stretch-info" 
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                color: 'white',
                borderRadius: '8px',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                transition: 'all 0.3s ease',
                border: '1px solid rgba(245, 158, 11, 0.4)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(245, 158, 11, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.3)';
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
              Advertised Location Information
            </a>
            <button
              onClick={() => setShowPuneMapModal(true)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                width: '100%', marginTop: '10px',
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                color: 'white', borderRadius: '8px', border: 'none',
                fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 16px rgba(124,58,237,0.4)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(124,58,237,0.3)'; }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
              View Pune-GA Map
            </button>
            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center' }}>
              <SocialShareButtons variant="inline" size={32} showLabel={true} />
            </div>
          </Footer>
        </Card>
      </RightSection>
      
      {/* Land Eligibility Modal */}
      {showEligibilityModal && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 99998,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            animation: 'eligFadeIn 0.3s ease',
            padding: '16px',
          }}
          onClick={() => setShowEligibilityModal(false)}
          onKeyDown={(e) => { if (e.key === 'Escape') setShowEligibilityModal(false); }}
          tabIndex={-1}
          ref={(el) => el?.focus()}
        >
          <style>{`
            @keyframes eligFadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes eligSlideUp { from { opacity: 0; transform: translateY(30px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
            @keyframes eligShimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
            @keyframes eligPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
          `}</style>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff',
              borderRadius: '24px',
              width: '100%',
              maxWidth: '480px',
              boxShadow: '0 25px 60px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.1)',
              animation: 'eligSlideUp 0.4s cubic-bezier(0.16,1,0.3,1)',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {/* Close button */}
            <button
              onClick={() => setShowEligibilityModal(false)}
              title="Close"
              style={{
                position: 'absolute', top: '16px', right: '16px', zIndex: 3,
                width: '32px', height: '32px', borderRadius: '50%',
                background: 'rgba(255,255,255,0.9)', border: '1px solid rgba(0,0,0,0.08)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#94a3b8', padding: 0, lineHeight: 0,
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#475569'; e.currentTarget.style.transform = 'scale(1.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.9)'; e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            {/* Gradient Header Banner */}
            <div style={{
              background: 'linear-gradient(135deg, #065f46 0%, #047857 40%, #059669 100%)',
              padding: '28px 28px 24px',
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* Decorative circles */}
              <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '100px', height: '100px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />
              <div style={{ position: 'absolute', bottom: '-30px', left: '-10px', width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
              
              {/* Icon */}
              <div style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: '52px', height: '52px', borderRadius: '14px',
                background: 'rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                marginBottom: '16px',
                border: '1px solid rgba(255,255,255,0.2)',
              }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </div>
              <h3 style={{
                margin: '0 0 6px', fontSize: '1.35rem', fontWeight: 800,
                color: '#ffffff', lineHeight: 1.3, letterSpacing: '-0.02em',
              }}>
                <span style={{ color: '#6ee7b7' }}>Great!</span> Let's get you started
              </h3>
              <p style={{
                margin: 0, fontSize: '0.85rem', color: 'rgba(255,255,255,0.8)',
                lineHeight: 1.5, maxWidth: '380px',
              }}>
                One quick check before you proceed
              </p>
            </div>

            {/* Body Content */}
            <div style={{ padding: '24px 28px 28px' }}>
              {/* Info card */}
              <div style={{
                background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '22px',
                border: '1px solid #fde68a',
                display: 'flex', alignItems: 'flex-start', gap: '12px',
              }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0,
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="16" x2="12" y2="12" />
                    <line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                </div>
                <p style={{
                  margin: 0, fontSize: '0.85rem', color: '#78350f',
                  lineHeight: 1.55, fontWeight: 500,
                }}>
                  Have you checked if the land or property you plan to offer MNGL is in the
                  <strong style={{ color: '#b45309' }}> list of advertised locations in Pune</strong>?
                </p>
              </div>

              {/* Action buttons */}
              <div style={{
                display: 'flex', gap: '12px',
                flexWrap: 'wrap',
              }}>
                <a
                  href="/area-stretch-info"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setShowEligibilityModal(false)}
                  style={{
                    flex: '1 1 200px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    padding: '13px 20px', borderRadius: '12px',
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    color: '#fff', textDecoration: 'none',
                    fontSize: '0.88rem', fontWeight: 700, textAlign: 'center' as const,
                    boxShadow: '0 4px 14px rgba(5,150,105,0.3)',
                    transition: 'all 0.25s ease',
                    border: 'none',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(5,150,105,0.4)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(5,150,105,0.3)'; }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  Sure, let me check the list
                </a>
                <button
                  onClick={() => setShowEligibilityModal(false)}
                  style={{
                    flex: '1 1 180px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                    padding: '13px 20px', borderRadius: '12px',
                    background: '#ffffff',
                    color: '#64748b',
                    fontSize: '0.85rem', fontWeight: 600, textAlign: 'center' as const,
                    border: '1.5px solid #e2e8f0',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#94a3b8'; e.currentTarget.style.color = '#334155'; e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.background = '#ffffff'; }}
                >
                  I will check it later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Brochure Modal */}
      {showBrochureModal && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={() => setShowBrochureModal(false)}
        >
          <ModalContainer
            $size={modalSize}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <ModalTitle>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                </svg>
                MNGL DODO Brochure
              </ModalTitle>
              
              <ModalControls>
                <a
                  href="/brochures/mngl-dodo-brochure.pdf"
                  download="MNGL-DODO-Brochure.pdf"
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    color: 'white',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <DownloadIcon />
                  Download
                </a>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ 
                    fontSize: '10px', 
                    color: 'rgba(255,255,255,0.7)', 
                    background: 'rgba(255,255,255,0.15)', 
                    padding: '4px 8px', 
                    borderRadius: '4px',
                    fontWeight: '500'
                  }}>ESC to close</span>
                  <ModalCloseButton onClick={() => setShowBrochureModal(false)} title="Close (ESC)">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </ModalCloseButton>
                </div>
              </ModalControls>
            </ModalHeader>
            <ModalBody>
              <PDFViewer
                src="/brochures/mngl-dodo-brochure.pdf"
                title="MNGL DODO Brochure"
              />
            </ModalBody>
          </ModalContainer>
        </ModalOverlay>
      )}
    </Container>
    <ChatWidget mode="visitor" fabPosition="top-right" fabLabel="Chat" />
    <SocialProofToast variant="toast" />
    <PuneMapModal open={showPuneMapModal} onClose={() => setShowPuneMapModal(false)} />
    </>
  );
};

export default EmailVerification;
